/**
 * Public satisFIND CMS shell. Wraps all publicly-rendered CMS pages,
 * articles and the /blog index in a consistent header + responsive
 * container so published content never looks like the Admin UI.
 *
 * Also owns the CMS-content CSS variables/class rules referenced from
 * the sanitizer's YouTube wrapper (`.cms-video`) and image wrappers.
 * Kept as inline `<style>` so this component is fully self-contained
 * and can't drift from the sanitizer's HTML shape.
 */
import React from 'react';
import { Link } from 'react-router-dom';

export function PublicShell({ children, canonicalPath }) {
  return (
    <div className="min-h-screen bg-white flex flex-col">
      <PublicShellStyles />
      <header className="border-b border-[#E2E8F0] bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between">
          <Link to="/satisfinder" className="flex items-center gap-2">
            <img src="/satisfind-logo.png" alt="satisFIND" style={{ height: 32, width: 'auto' }} />
          </Link>
          <nav className="flex items-center gap-4 text-sm">
            <Link to="/blog" className="text-[#334155] hover:text-[#0F172A]">Blog</Link>
            <Link to="/satisfinder/login" className="text-[#334155] hover:text-[#0F172A]">Login</Link>
          </nav>
        </div>
      </header>
      <main className="flex-1">
        {children}
      </main>
      <footer className="border-t border-[#E2E8F0] bg-[#F8FAFC] mt-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 text-xs text-[#64748B] flex flex-wrap items-center justify-between gap-2">
          <span>© {new Date().getFullYear()} satisFIND</span>
          <span className="opacity-70">{canonicalPath}</span>
        </div>
      </footer>
    </div>
  );
}

/**
 * Rendered-content CSS. Scoped by `.cms-body` so it never leaks to
 * Admin surfaces. Handles:
 *   • responsive images (never overflow, keep aspect ratio)
 *   • wide tables (horizontal scroll inside their own container)
 *   • YouTube embeds (16/9 aspect-ratio box that resizes cleanly)
 *   • long URLs / text (safe wrapping)
 *   • responsive typography scaling
 */
function PublicShellStyles() {
  return (
    <style>{`
      .cms-body { color: #0F172A; font-size: 16px; line-height: 1.7;
        word-wrap: break-word; overflow-wrap: anywhere; }
      .cms-body h1 { font-size: 1.875rem; font-weight: 700; line-height: 1.2; margin: 1.5rem 0 1rem; }
      .cms-body h2 { font-size: 1.5rem;   font-weight: 700; line-height: 1.25; margin: 1.5rem 0 0.75rem; }
      .cms-body h3 { font-size: 1.25rem;  font-weight: 700; line-height: 1.3;  margin: 1.25rem 0 0.5rem; }
      .cms-body h4 { font-size: 1.125rem; font-weight: 700; line-height: 1.3;  margin: 1rem 0 0.5rem; }
      .cms-body h5 { font-size: 1rem;     font-weight: 700; line-height: 1.3;  margin: 1rem 0 0.5rem; }
      .cms-body h6 { font-size: 0.875rem; font-weight: 700; line-height: 1.3;  margin: 1rem 0 0.5rem; }
      .cms-body p, .cms-body ul, .cms-body ol, .cms-body blockquote { margin: 0 0 1rem; }
      .cms-body ul, .cms-body ol { padding-left: 1.5rem; }
      .cms-body ul { list-style: disc; }
      .cms-body ol { list-style: decimal; }
      .cms-body li { margin: 0.25rem 0; }
      .cms-body a { color: #1A3E6F; text-decoration: underline; }
      .cms-body a:hover { color: #12305A; }
      .cms-body strong, .cms-body b { font-weight: 700; }
      .cms-body em, .cms-body i { font-style: italic; }
      .cms-body u { text-decoration: underline; }
      .cms-body blockquote { border-left: 4px solid #CBD5E1; padding: 0.25rem 1rem;
        color: #475569; font-style: italic; background: #F8FAFC; }
      .cms-body hr { border: none; border-top: 1px solid #E2E8F0; margin: 2rem 0; }
      .cms-body img { max-width: 100%; height: auto; display: block; margin: 1rem auto;
        border-radius: 4px; }
      .cms-body figure { margin: 1rem 0; }
      .cms-body figure figcaption { text-align: center; font-size: 0.8125rem; color: #64748B; margin-top: 0.25rem; }
      /* Wide-table safety — scroll horizontally inside the content column. */
      .cms-body table { display: block; overflow-x: auto; max-width: 100%;
        border-collapse: collapse; width: 100%; margin: 1rem 0; }
      .cms-body th, .cms-body td { border: 1px solid #E2E8F0; padding: 0.5rem 0.75rem; text-align: left; vertical-align: top; }
      .cms-body th { background: #F8FAFC; font-weight: 600; }
      .cms-body code { background: #F1F5F9; padding: 0.125rem 0.375rem; border-radius: 3px; font-size: 0.9em; }
      .cms-body pre { background: #0F172A; color: #F8FAFC; padding: 1rem; border-radius: 6px; overflow-x: auto; }
      /* Responsive YouTube / iframe embeds — sanitizer wraps every iframe
         in <figure class="cms-video">. */
      .cms-body .cms-video { position: relative; width: 100%; padding-top: 56.25%;
        margin: 1rem 0; background: #000; border-radius: 6px; overflow: hidden; }
      .cms-body .cms-video iframe { position: absolute; top: 0; left: 0;
        width: 100%; height: 100%; border: 0; }
      /* Mobile typography tune-up. */
      @media (max-width: 480px) {
        .cms-body { font-size: 15.5px; }
        .cms-body h1 { font-size: 1.625rem; }
        .cms-body h2 { font-size: 1.375rem; }
        .cms-body h3 { font-size: 1.125rem; }
      }
    `}</style>
  );
}
